'use client';

import { c as _c } from "react/compiler-runtime";
import { ArrowLeft, Download } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { Button } from '@/shared/components/ui';
import { NotFoundState } from '@/shared/components/ui/common/NotFoundState';
import { useCertificadoNegociacao } from '../../../hooks/useCertificadoNegociacao';
import { useSecoesDoCertificado } from '../../../hooks/useSecoesDoCertificado';
import { useTempoRealDoCertificado } from '../../../hooks/useTempoRealDoCertificado';
import type { AvisoDoCertificadoProps, CertificadoNegociacaoPageProps } from '../../../types/certificadoNegociacaoTypes';
import { AVISO_DE_VINCULO_A_RECONCILIAR, ehVinculoAReconciliar } from '../../../utils/certificadoNegociacao';
import { CertificadoNegociacaoDocumento } from './CertificadoNegociacaoDocumento';
const AVISO_DE_FALHA = 'Não foi possível carregar todos os registros.';
function CarregandoRegistros() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <p role="status" className="text-subtext-color mb-4 text-sm">Carregando registros do certificado…</p>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function AvisoDoCertificado(t0) {
  const $ = _c(7);
  const {
    texto,
    aoTentarNovamente
  } = t0;
  let t1;
  if ($[0] !== texto) {
    t1 = <p>{texto}</p>;
    $[0] = texto;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== aoTentarNovamente) {
    t2 = aoTentarNovamente && <Button variant="neutral-secondary" size="small" onClick={aoTentarNovamente}>Tentar novamente</Button>;
    $[2] = aoTentarNovamente;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div role="alert" className="border-error-100 bg-error-50 text-error-700 mb-4 flex w-full max-w-[794px] flex-wrap items-center justify-between gap-3 rounded-[16px] border p-4 text-sm">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
export function CertificadoNegociacaoPageContainer(t0) {
  const $ = _c(46);
  const {
    caseId,
    baixarAoAbrir: t1
  } = t0;
  const baixarAoAbrir = t1 === undefined ? false : t1;
  const router = useRouter();
  let t2;
  if ($[0] !== baixarAoAbrir) {
    t2 = {
      baixarAoAbrir
    };
    $[0] = baixarAoAbrir;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    estado,
    baixandoPdf,
    tentarNovamente,
    baixarPdf
  } = useCertificadoNegociacao(caseId, t2);
  const certificado = estado.fase === "pronto" ? estado.certificado : null;
  const {
    documento,
    carregamento,
    sentinelaRef,
    tentarNovamente: tentarPaginaDeNovo
  } = useSecoesDoCertificado(caseId, certificado?.emissao.id ?? null);
  useTempoRealDoCertificado();
  const erro = estado.fase === "erro" ? estado.erro : null;
  let t3;
  if ($[2] !== erro) {
    t3 = ehVinculoAReconciliar(erro);
    $[2] = erro;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const vinculoAReconciliar = t3;
  if (erro?.status === 404) {
    let t4;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <NotFoundState title={"Certificado n\xE3o encontrado"} description={"N\xE3o foi poss\xEDvel carregar o certificado desta negocia\xE7\xE3o. Volte \xE0 negocia\xE7\xE3o e tente novamente."} />;
      $[4] = t4;
    } else {
      t4 = $[4];
    }
    return t4;
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <ArrowLeft className="size-4" />;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== caseId || $[7] !== router) {
    t5 = () => router.push(`/negociacoes/${encodeURIComponent(caseId)}?tab=certificado`);
    $[6] = caseId;
    $[7] = router;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== baixandoPdf || $[10] !== t5) {
    t6 = <Button variant="neutral-tertiary" icon={t4} disabled={baixandoPdf} onClick={t5}>Voltar</Button>;
    $[9] = baixandoPdf;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Download className="size-4" />;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const t8 = !certificado || baixandoPdf;
  let t9;
  if ($[13] !== baixarPdf) {
    t9 = () => void baixarPdf();
    $[13] = baixarPdf;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== baixandoPdf || $[16] !== t8 || $[17] !== t9) {
    t10 = <Button icon={t7} loading={baixandoPdf} disabled={t8} onClick={t9}>Baixar PDF</Button>;
    $[15] = baixandoPdf;
    $[16] = t8;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== t10 || $[20] !== t6) {
    t11 = <div className="flex w-full max-w-[794px] items-center justify-between gap-3 pb-5" data-doc-print-hidden={true}>{t6}{t10}</div>;
    $[19] = t10;
    $[20] = t6;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== estado.fase) {
    t12 = estado.fase === "carregando" && <CarregandoRegistros />;
    $[22] = estado.fase;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== erro || $[25] !== tentarNovamente || $[26] !== vinculoAReconciliar) {
    t13 = erro && <AvisoDoCertificado texto={vinculoAReconciliar ? AVISO_DE_VINCULO_A_RECONCILIAR : AVISO_DE_FALHA} aoTentarNovamente={vinculoAReconciliar ? undefined : tentarNovamente} />;
    $[24] = erro;
    $[25] = tentarNovamente;
    $[26] = vinculoAReconciliar;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== certificado || $[29] !== documento) {
    t14 = certificado && <div className="w-full max-w-full overflow-x-auto"><div data-doc-print-root={true} data-dd-privacy="mask" className="mx-auto flex w-fit flex-col gap-6 pb-6"><CertificadoNegociacaoDocumento certificado={certificado} secoes={documento} /></div></div>;
    $[28] = certificado;
    $[29] = documento;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== carregamento || $[32] !== certificado || $[33] !== sentinelaRef) {
    t15 = certificado && carregamento === "carregando" && <><div ref={sentinelaRef} aria-hidden={true} data-testid="certificado-sentinela" /><CarregandoRegistros /></>;
    $[31] = carregamento;
    $[32] = certificado;
    $[33] = sentinelaRef;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== carregamento || $[36] !== certificado || $[37] !== tentarPaginaDeNovo) {
    t16 = certificado && carregamento === "falhou" && <AvisoDoCertificado texto={AVISO_DE_FALHA} aoTentarNovamente={tentarPaginaDeNovo} />;
    $[35] = carregamento;
    $[36] = certificado;
    $[37] = tentarPaginaDeNovo;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  let t17;
  if ($[39] !== t11 || $[40] !== t12 || $[41] !== t13 || $[42] !== t14 || $[43] !== t15 || $[44] !== t16) {
    t17 = <div className="flex w-full flex-col items-center bg-neutral-100 px-4 py-6 sm:py-10">{t11}{t12}{t13}{t14}{t15}{t16}</div>;
    $[39] = t11;
    $[40] = t12;
    $[41] = t13;
    $[42] = t14;
    $[43] = t15;
    $[44] = t16;
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  return t17;
}